import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import _ from 'underscore';
import classnames from 'classnames';

import {
  useImageExperienceAdPreviewModel,
  AdPreviewCarouselLayout,
  UAPPlacementTypeAlias as EPT,
} from '@bingads-webui-campaign-react/unified-ad-preview-library-cmui';

import getCroppedImage from '../util/get-cropped-image';
import getGenericImageObjectAsync from '../util/get-generic-image-object';
import { ImageExperienceImage } from '../model/image-experience-image';
import C from '../constants';

import { getOnRenderPlacement } from './unified-placement-render';

const adPreviewConfig = [{
  placementSetName: 'Carousel133x100',
  placementTypes: [
    EPT.ImageExt.BingMobileMultiImage,
    EPT.ImageExt.BingDesktopMultiImage,
    EPT.ImageExt.MSNSmallHeadline,
  ],
}];

export const UnifiedCarouselPreview133x100 = (props) => {
  const {
    image,
    genericImage,
    placeHolderImages: placeHolderImagesProp,
    previewTexts,
    i18n,
    classes,
  } = props;

  const [imagesForDisplay, setImagesForDisplay] = useState([]);

  useEffect(() => {
    const imagesToSetup = [
      genericImage,
      ...placeHolderImagesProp,
    ];

    const setupPromises = _.map(imagesToSetup, imageToSetup => getGenericImageObjectAsync(imageToSetup).then((imageObj) => {
      const croppedImages = getCroppedImage({ originalImageToCrop: imageObj });
      return croppedImages;
    }));

    Promise.all(setupPromises).then(([defaultImages, ...placeHolderImagesList]) => {
      let placeHolderImages = _.isEmpty(placeHolderImagesList) ? [defaultImages] : placeHolderImagesList;
      placeHolderImages = _.map(placeHolderImages, (imageObjs) => {
        const image133x100List = _.filter(imageObjs, img => img.type === C.aspectRatios.Image133x100.type);
        return image133x100List;
      });
      setImagesForDisplay([[image], ...placeHolderImages]);
    });
  }, [genericImage, image, placeHolderImagesProp]);

  const {
    multiImagePlacementSets: placementSets,
    onUpdateImageExperienceImageList,
  } = useImageExperienceAdPreviewModel(adPreviewConfig);

  const placementList = useMemo(() => {
    const list = _.chain(placementSets).map(placementSet => placementSet.placementList).flatten().value();
    return list;
  }, [placementSets]);

  useEffect(() => {
    onUpdateImageExperienceImageList(imagesForDisplay, null);
  }, [imagesForDisplay, onUpdateImageExperienceImageList]);

  const onRenderPlacement = useMemo(() => {
    const params = {
      isMultimediaAd: false,
      isMultiMediaAdsPhase2Enabled: false,
      previewTexts,
      i18n,
    };
    return getOnRenderPlacement(params, classes.previewPlacementContainerRoot);
  }, [classes.previewPlacementContainerRoot, i18n, previewTexts]);

  return (
    <AdPreviewCarouselLayout
      placementList={placementList}
      onRenderPlacement={onRenderPlacement}
      enableAutoRotate={false}
      i18n={i18n}
      disableDefaultStyles
      customCarouselLayoutStyles={classnames('edit-carousel-container', classes.adPreviewCarousel133x100Container)}
    />
  );
};

UnifiedCarouselPreview133x100.propTypes = {
  previewTexts: PropTypes.shape({
    longHeadline: PropTypes.string,
    description: PropTypes.string,
    businessName: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
  image: PropTypes.instanceOf(ImageExperienceImage).isRequired,
  genericImage: PropTypes.string.isRequired,
  placeHolderImages: PropTypes.arrayOf(PropTypes.string),
};

UnifiedCarouselPreview133x100.defaultProps = {
  placeHolderImages: [],
};
